import React from "react";
import moment from "moment";
import {
  MultiSelectTree,
  MultiSelectTreeChangeEvent,
  MultiSelectTreeExpandEvent,
  getMultiSelectTreeValue,
} from "@progress/kendo-react-dropdowns";
import {
  processMultiSelectTreeData,
  expandedState,
} from "../../Pages/MultiselectTree-Data-Operations";

const treeFields = {
  dataItemKey: "id",
  checkField: "checkField",
  checkIndeterminateField: "checkIndeterminateField",
  expandField: "expanded",
  subItemsField: "items",
} as const;

type PeriodMode = "months" | "weeks";

// Formats per mode. Weeks use the ISO week-year (GGGG/GG) so a week near the
// year boundary groups under the year its ISO week belongs to — not the
// calendar year of the date (e.g. 31/Dec/2024 is ISO week 1 of 2025).
const formatsFor = (mode: PeriodMode) =>
  mode === "months"
    ? { unit: "month", idFmt: "MMM-YY", textFmt: "MMMM", yearFmt: "YYYY" }
    : { unit: "week", idFmt: "[W]WW-GG", textFmt: "[Week] WW", yearFmt: "GGGG" };

// The `n` most recent period ids (newest first) — used to seed the default selection.
export const recentPeriods = (mode: PeriodMode, n: number): string[] => {
  const { unit, idFmt } = formatsFor(mode);
  return Array.from({ length: n }, (_, i) =>
    moment().subtract(i, unit as any).format(idFmt)
  );
};

// Year-grouped period leaves for the last `yearsBack` years (newest first).
const buildGroups = (mode: PeriodMode, yearsBack: number) => {
  const { unit, idFmt, textFmt, yearFmt } = formatsFor(mode);
  const count = (mode === "months" ? 12 : 53) * yearsBack;
  const byYear: Record<string, { id: string; text: string }[]> = {};
  const order: string[] = [];
  for (let i = 0; i < count; i++) {
    const d = moment().subtract(i, unit as any);
    const year = d.format(yearFmt);
    if (!byYear[year]) {
      byYear[year] = [];
      order.push(year);
    }
    byYear[year].push({ id: d.format(idFmt), text: d.format(textFmt) });
  }
  return order.map((year) => ({ id: year, text: year, items: byYear[year] }));
};

interface Props {
  mode: PeriodMode;
  value: string[];
  onChange: (values: string[]) => void;
  yearsBack?: number;
  width?: number | string;
  /** Max selectable periods (backend caps the report at 6). */
  max?: number;
  disabled?: boolean;
}

// Multiselect tree grouped by year: only the periods (leaves) are selectable,
// years act as expandable headers. No "select all" — selection is capped at 6.
export default function PeriodYearTree({
  mode,
  value,
  onChange,
  yearsBack = 3,
  width = 220,
  max = 6,
  disabled = false,
}: Props) {
  const groups = React.useMemo(() => buildGroups(mode, yearsBack), [mode, yearsBack]);
  const leafIds = React.useMemo(
    () => new Set(groups.flatMap((g) => g.items).map((l) => l.id)),
    [groups]
  );

  const [expanded, setExpanded] = React.useState<string[]>([
    moment().format(mode === "months" ? "YYYY" : "GGGG"),
  ]);

  const treeValue = groups
    .flatMap((g) => g.items)
    .filter((leaf) => value.includes(leaf.id));

  const treeData = processMultiSelectTreeData(groups, {
    expanded,
    value: treeValue,
    ...treeFields,
  });

  // Default MultiSelectTree look — shows a chip per selected period (max 6).
  return (
    <div style={{ width }}>
      <MultiSelectTree
        style={{ width }}
        disabled={disabled}
        data={treeData}
        value={treeValue}
        textField="text"
        dataItemKey={treeFields.dataItemKey}
        checkField={treeFields.checkField}
        checkIndeterminateField={treeFields.checkIndeterminateField}
        subItemsField={treeFields.subItemsField}
        expandField={treeFields.expandField}
        onExpandChange={(event: MultiSelectTreeExpandEvent) =>
          setExpanded((prev) => expandedState(event.item, treeFields.dataItemKey, prev))
        }
        // Chip shows the period id (e.g. "Jul-26") — same as the Focus filter.
        tags={treeValue.map((leaf) => ({ text: leaf.id, data: [leaf] }))}
        onChange={(event: MultiSelectTreeChangeEvent) => {
          const picked = getMultiSelectTreeValue(groups, {
            ...treeFields,
            ...event,
            value: treeValue,
          })
            .map((item: any) => item.id as string)
            .filter((id: string) => leafIds.has(id)); // drop year parents
          onChange(picked.slice(0, max));
        }}
        popupSettings={{
          className: "my-multiselect-popup period-year-tree-popup",
          width,
        }}
      />
    </div>
  );
}
